import * as React from 'react';
import { Toast } from 'primereact/toast';
import { useState, useEffect } from "react";
import parse, { HTMLReactParserOptions } from 'html-react-parser';
import { s3GetUrl } from "../../service/s3Service";
import {getDocumentSignedById, DocumentSignedDetails, DocumentSigned} from "../../service/documentsSignedService";
import dayjs from 'dayjs';
import {formatDate, formatDateTime} from "../../utils/DateUtils";
import {Skeleton} from "primereact/skeleton";

interface EmployeeDocumentsSignedItemProps {
  id: string;
  record: DocumentSigned;
  setGlobalLoading: (loading: boolean) => void;
}

export default function EmployeeDocumentsSignedItem({ id, record, setGlobalLoading }: EmployeeDocumentsSignedItemProps) {
  const toast = React.useRef<Toast>(null);
  const [document, setDocument] = useState<DocumentSignedDetails['document']>();
  const [documentSignatures, setDocumentSignatures] = useState<DocumentSignedDetails['document_signatures']>([]);
  const [html, setHtml] = useState<any>([]);

  async function fetchData() {
    setGlobalLoading(true);
    try {
      const resp = await getDocumentSignedById(id);
      setDocument(resp.content.document);
      setDocumentSignatures(resp.content.document_signatures);
    } catch (e) {
      console.error("Error fetching document:", e);
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Failed to fetch document', life: 3000 });
      }
    } finally {
      setGlobalLoading(false);
    }
  }

  function renderSignatureContainer(signatureUrl?: string, altText: string = "Signature not available") {
    return (
      <div
        className="signature-image-container"
        style={{
          height: "80px",
          width: "250px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          margin: "5px 0"
        }}
      >
        {signatureUrl ? (
          <img
            src={signatureUrl}
            alt="Signature"
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        ) : (
          <span>{altText}</span>
        )}
      </div>
    );
  }

  async function renderHtml() {
    if (!document || !documentSignatures.length) {
      return;
    }

    let rawDocument = document.content;
    const replacements = Object.entries(JSON.parse(document.replacements));

    // Apply all replacements
    for (const [key, value] of replacements) {
      if(key.endsWith("_list")) {
        const htmlListItems = (value as string[]).map((item: string) =>
          `<li style="list-style-type: inherit;padding-left: 0;position: inherit;">${item}</li>`
        ).join("");
        rawDocument = rawDocument.replaceAll(`{${key}}`, `<ul>${htmlListItems}</ul>`);
        continue;
      }
      rawDocument = rawDocument.replaceAll(`{${key}}`, value as string);
    }

    for(const sig of documentSignatures) {
      rawDocument = rawDocument.replaceAll(sig.signature_place_holder_name, sig.signature_name);
      if(sig.signed_at) {
        const signedAt = dayjs(sig.signed_at).format('D MMMM YYYY');
        rawDocument = rawDocument.replaceAll(sig.signature_place_holder_signed_at, signedAt);
      }
    }

    // Prefetch all signature URLs
    const signatureUrls: { [key: string]: string | undefined } = {};
    for (const signature of documentSignatures) {
      if (signature.signed_at) {
        try {
          signatureUrls[signature.signature_place_holder] = await s3GetUrl(signature.signature_s3_key);
        } catch (e) {
          console.error("Error loading signature:", e);
          signatureUrls[signature.signature_place_holder] = undefined;
        }
      }
    }

    // Replace signature placeholders with actual signatures
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (domNode.type === "text" &&
          domNode.data.startsWith("{appointment__") && domNode.data.endsWith("__signature}")) {

          const signature = documentSignatures
            .find(sig => sig.signature_place_holder === domNode.data);
          if (!signature) return;

          const signatureUrl = signatureUrls[signature.signature_place_holder];
          return renderSignatureContainer(signatureUrl, signatureUrl ? "Signature" : "Not signed yet");
        }
      },
    };

    setHtml(parse(rawDocument, options));
  }

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    renderHtml();
  }, [document, documentSignatures]);

  useEffect(() => {
    console.log("HTML", html)
  }, [html]);

  return (
    <div className="renderedForm">
      <div style={{backgroundColor: "#dcdcdc", padding: "20px", borderRadius: "10px", marginBottom: "10px", display: "flex", flexWrap: "wrap", gap: "10px"}}>
        <div style={{flex: 1, minWidth: 300}}>
          <p className="doc-sign-info-item"><span style={{fontWeight: "bold"}}>Site</span>: {record.site_name}</p>
          {record.document_appointment_name && (
            <p className="doc-sign-info-item"><span style={{fontWeight: "bold"}}>Appointment</span>: {record.document_appointment_name}</p>
          )}
          {record.document_template_name && (
            <p className="doc-sign-info-item"><span style={{fontWeight: "bold"}}>Policy & Procedure</span>: {record.document_template_name}</p>
          )}
          <p className="doc-sign-info-item"><span style={{fontWeight: "bold"}}>Created At</span>: {formatDateTime(record.created_at)}</p>
        </div>
        <div style={{flex: 1, minWidth: 300}}>
          <p className="doc-sign-info-item"><span style={{fontWeight: "bold"}}>Employee</span>: {record.employee_name}</p>
          <p className="doc-sign-info-item"><span style={{fontWeight: "bold"}}>Signed Location</span>: {record.signed_location}</p>
          <p className="doc-sign-info-item"><span style={{fontWeight: "bold"}}>Signed At</span>: {formatDateTime(record.signed_at)}</p>
        </div>
      </div>
      <div className="ql-editor">
        {html}
        {html.length === 0 && (
          <div style={{marginTop: 40}}>
            <Skeleton width="40%" height="3rem" className="mb-5"></Skeleton>

            <Skeleton width="50%" className="mb-2"></Skeleton>
            <Skeleton width="30%" className="mb-2"></Skeleton>
            <Skeleton width="70%" className="mb-4"></Skeleton>
            <Skeleton width="80%" className="mb-2"></Skeleton>
            <Skeleton width="40%" className="mb-2"></Skeleton>
            <Skeleton width="60%" className="mb-5"></Skeleton>

            <Skeleton width="30%" height="1.5rem" className="mb-2"></Skeleton>
            <Skeleton width="300px" height="4rem"></Skeleton>
            <Skeleton width="20%" className="mt-2 mb-2"></Skeleton>

          </div>
        )}
      </div>
      <Toast ref={toast} />
    </div>
  );
}
